/**
 * Button — actions ranked by how loud they should be (design system):
 * `primary` (brandFill, one per screen area: "New job"), `secondary`
 * (`washStrong` tint + `borderStrong` outline: "Edit", "Export"), `quiet`
 * (text only: "Archive"), `danger` (danger outline). Never a white button.
 * 40px tall, `button` text role; `density="compact"` is 36px (44px on a
 * phone) with tighter padding.
 *
 * Secondary is a tint over whatever it sits on, so it always reads a step
 * lighter than its box in dark mode (and a step darker in light), on `bg`,
 * `bgSubtle` or `surface` alike. It used to be `surfaceRaised`, which in
 * dark (#2A2A2A) is as dark as `surface` (#1F2937) and read as a hole.
 */
import React, { useState } from 'react';
import { compactSpec, compactVars, textRole, tokenVars, type Density } from '@turn-connect/ui';
import { useDensity } from './density';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** `compact` (Reports, tables): 36px, 44px on a phone. Defaults to the nearest DensityProvider, else `default` (40px). */
  density?: Density;
  'data-testid'?: string;
}

/** Each variant's colors (exported for tests). */
export const BUTTON_LOOK: Record<ButtonVariant, { bg: string; hover: string; color: string; border: string }> = {
  primary: { bg: tokenVars.brandFill, hover: tokenVars.brandFillPressed, color: tokenVars.onAccent, border: tokenVars.brandFill },
  secondary: {
    bg: tokenVars.washStrong,
    // Twice the tint: one step further from the ground in both themes.
    hover: `linear-gradient(${tokenVars.washStrong}, ${tokenVars.washStrong}), ${tokenVars.washStrong}`,
    color: tokenVars.text.primary,
    border: tokenVars.borderStrong,
  },
  quiet: { bg: 'transparent', hover: tokenVars.wash, color: tokenVars.text.secondary, border: 'transparent' },
  danger: { bg: 'transparent', hover: tokenVars.dangerSubtle, color: tokenVars.dangerText, border: tokenVars.danger },
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', density, style, disabled, children, type = 'button', onMouseEnter, onMouseLeave, ...rest },
  ref
) {
  const [hover, setHover] = useState(false);
  const look = BUTTON_LOOK[variant];
  const compact = useDensity(density) === 'compact';
  const padX = compact ? compactSpec.button.paddingHorizontal : 16;
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      data-variant={variant}
      data-density={compact ? 'compact' : undefined}
      {...rest}
      onMouseEnter={(e) => {
        setHover(true);
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setHover(false);
        onMouseLeave?.(e);
      }}
      style={{
        ...textRole('button', look.color),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        // 40px on a computer; on a phone the compact control height is 44, so both densities reach 44.
        minHeight: compact ? compactVars.controlHeight : `max(40px, ${compactVars.controlHeight})`,
        padding: variant === 'quiet' ? '0 8px' : `0 ${padX}px`,
        borderRadius: 6,
        border: `1px solid ${look.border}`,
        background: hover && !disabled ? look.hover : look.bg,
        fontFamily: 'inherit',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
});

export default Button;
